import { useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * Create-agent flow — a guided stepper that mints a policy, mapped 1:1 to the
 * Move `Policy` struct (altheia-sui/sources/policy.move):
 *   per_day_cap (required) · per_tx_cap (optional, 0=off) ·
 *   allowed_actions: VecSet<u8> (default-deny; swap=1 limit=2 cancel=3 transfer=0) ·
 *   action_params (per-action config — swap min-rate = the value-conservation floor) ·
 *   allowed_packages (scope) · expires_at_ms.
 *
 * DESIGN.md (Persian + Orange): ghost ground, indigo structure, orange = live
 * only, coral = denial. emil: stepped transitions, progress fill, press feedback,
 * reduced-motion safe. impeccable: one primary action per step, no slop. taste:
 * full-width composition, the policy object is the artifact (no dead space).
 */

export type Asset = string; // coin symbol (SUI/USDC/DEEP) or a pasted coin-type id
export type ActionId = "deepbook_swap" | "deepbook_limit_order" | "deepbook_cancel" | "transfer";

// The coins DeepBook trades on testnet — the "add token" picker offers these;
// advanced users paste any coin-type id.
export const POPULAR_COINS = ["SUI", "USDC", "DEEP"];
const COIN_NAME: Record<string, string> = { SUI: "Sui", USDC: "USD Coin", DEEP: "DeepBook" };

export type AssetCap = { coin: Asset; per_day: number; per_tx?: number };
export type Policy = {
  name: string;
  caps: AssetCap[]; // caps[0] is the funded budget asset; the rest are capped, funded as acquired
  allowed_actions: ActionId[];
  action_params: { deepbook_swap_min_rate?: number };
  scope: { pkg: "deepbook"; pool?: string };
  expiry_hours: number;
};

const ACTIONS: { id: ActionId; label: string; hint: string }[] = [
  { id: "deepbook_swap", label: "Swap", hint: "market swap, value-guarded" },
  { id: "deepbook_limit_order", label: "Limit order", hint: "place a resting order" },
  { id: "deepbook_cancel", label: "Cancel order", hint: "cancel own orders" },
  { id: "transfer", label: "Transfer", hint: "move coins out (exfiltration risk)" },
];

// Capabilities clubbed by how each is verified on-chain.
const ACTION_GROUPS: { group: string; verify: string; ids: ActionId[] }[] = [
  { group: "DeepBook trading", verify: "value-guarded + budget-capped", ids: ["deepbook_swap", "deepbook_limit_order", "deepbook_cancel"] },
  { group: "Custody", verify: "can only leave the vault if the asset has a cap", ids: ["transfer"] },
];

const STEPS = ["Identity", "Budget", "Capabilities", "Scope", "Lifetime", "Review"] as const;

const DEFAULT_POLICY: Policy = {
  name: "deepbook-mm-1",
  caps: [{ coin: "SUI", per_day: 2, per_tx: 0.5 }],
  allowed_actions: ["deepbook_swap"],
  action_params: { deepbook_swap_min_rate: 0.98 },
  scope: { pkg: "deepbook" },
  expiry_hours: 24,
};

function expiryLabel(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return "no expiry set";
  return new Date(Date.now() + hours * 3600_000).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Human-readable action names for the displayed/editable policy JSON. The
// numeric on-chain encoding (swap=1, etc.) is applied later in the SDK mapping,
// so the operator never sees raw ids here.
const ACTION_NAME: Record<ActionId, string> = { deepbook_swap: "swap", deepbook_limit_order: "limit_order", deepbook_cancel: "cancel", transfer: "transfer" };
const NAME_ACTION: Record<string, ActionId> = { swap: "deepbook_swap", limit_order: "deepbook_limit_order", cancel: "deepbook_cancel", transfer: "transfer" };

function toMoveObject(p: Policy): Record<string, unknown> {
  return {
    name: p.name,
    budget_asset: p.caps[0]?.coin ?? "SUI",
    per_asset_caps: p.caps.map((c) => ({ asset: c.coin, per_day: c.per_day, per_tx: c.per_tx ?? 0 })),
    allowed_actions: p.allowed_actions.map((a) => ACTION_NAME[a]),
    action_params: p.allowed_actions.includes("deepbook_swap") && p.action_params.deepbook_swap_min_rate != null
      ? { swap: { min_return_pct: Math.round((p.action_params.deepbook_swap_min_rate ?? 0) * 1000) / 10 } }
      : {},
    allowed_packages: [p.scope.pkg],
    ...(p.scope.pool ? { pool: p.scope.pool } : {}),
    expires_at_ms: hoursToExpiry(p.expiry_hours),
  };
}
function hoursToExpiry(h: number): number {
  return Number.isFinite(h) && h > 0 ? Date.now() + h * 3600_000 : 0;
}

export function SuiPolicyEditor({ initial, onSubmit = () => {} }: { initial?: Partial<Policy>; onSubmit?: (p: Policy) => void }) {
  const seed = useMemo<Policy>(() => ({ ...DEFAULT_POLICY, ...initial, caps: initial?.caps ?? DEFAULT_POLICY.caps, scope: { ...DEFAULT_POLICY.scope, ...(initial?.scope ?? {}) }, action_params: { ...DEFAULT_POLICY.action_params, ...(initial?.action_params ?? {}) } }), [initial]);
  const [policy, setPolicy] = useState<Policy>(seed);
  const [step, setStep] = useState(0);
  const [minted, setMinted] = useState(false);
  const [jsonEdit, setJsonEdit] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonErr, setJsonErr] = useState<string | null>(null);
  const update = (patch: (p: Policy) => Policy) => { setMinted(false); setPolicy(patch); };

  const swapOn = policy.allowed_actions.includes("deepbook_swap");
  const obj = toMoveObject(policy);
  const objJson = JSON.stringify(obj, null, 2);
  const slipPct = Math.round((1 - (policy.action_params.deepbook_swap_min_rate ?? 0.98)) * 1000) / 10;
  // Keep the editable JSON in sync as the operator toggles steps; their own
  // typed edits persist (obj only changes on a control change or Apply).
  useEffect(() => { if (jsonEdit) setJsonText(objJson); }, [objJson, jsonEdit]);

  const setCap = (i: number, patch: Partial<AssetCap>) => update((p) => ({ ...p, caps: p.caps.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  const addCap = (coin: string) => { update((p) => (p.caps.some((c) => c.coin === coin) ? p : { ...p, caps: [...p.caps, { coin, per_day: 100 }] })); setPickerOpen(false); setPasteCoin(""); };
  const removeCap = (i: number) => update((p) => ({ ...p, caps: p.caps.length > 1 ? p.caps.filter((_, j) => j !== i) : p.caps }));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pasteCoin, setPasteCoin] = useState("");
  const usedCoins = new Set(policy.caps.map((c) => c.coin));

  function openEditJson() { setJsonErr(null); setJsonEdit(true); }
  function applyJson() {
    try {
      const o = JSON.parse(jsonText) as Record<string, unknown>;
      const acts = ((o.allowed_actions as string[] | undefined) ?? []).map((n) => NAME_ACTION[n]).filter(Boolean) as ActionId[];
      const pct = (o.action_params as { swap?: { min_return_pct?: number } } | undefined)?.swap?.min_return_pct;
      const rate = pct != null ? pct / 100 : policy.action_params.deepbook_swap_min_rate;
      const exp = Number(o.expires_at_ms ?? 0);
      const pac = o.per_asset_caps as { asset?: string; per_day?: number; per_tx?: number }[] | undefined;
      const caps: AssetCap[] = pac && pac.length
        ? pac.map((c) => ({ coin: String(c.asset ?? "SUI"), per_day: Number(c.per_day ?? 0), per_tx: c.per_tx ? Number(c.per_tx) : undefined }))
        : policy.caps;
      update(() => ({
        name: String(o.name ?? policy.name),
        caps,
        allowed_actions: acts.length ? acts : policy.allowed_actions,
        action_params: { deepbook_swap_min_rate: rate },
        scope: { pkg: "deepbook", pool: (o.pool as string | undefined) || undefined },
        expiry_hours: exp > Date.now() ? Math.round((exp - Date.now()) / 3600_000) : policy.expiry_hours,
      }));
      setJsonErr(null);
      setJsonEdit(false);
    } catch {
      setJsonErr("invalid JSON — check syntax");
    }
  }

  // Per-step "can advance" gates — guide the user, don't let them mint nonsense.
  const stepValid = [
    policy.name.trim().length > 0,
    policy.caps.length > 0 && policy.caps.every((c) => c.per_day > 0),
    policy.allowed_actions.length > 0,
    true,
    policy.expiry_hours > 0,
    true,
  ];
  const canNext = stepValid[step];
  const isReview = step === STEPS.length - 1;

  function toggleAction(id: ActionId) {
    update((p) => {
      const has = p.allowed_actions.includes(id);
      const next = (has ? p.allowed_actions.filter((a) => a !== id) : [...p.allowed_actions, id]) as ActionId[];
      return { ...p, allowed_actions: next };
    });
  }

  return (
    <div className="ce">
      <header className="ce__head">
        <div className="ce__kick">sui · deepbook · mint a policy</div>
        <h1 className="ce__title">Create agent</h1>
      </header>

      {/* Step rail */}
      <ol className="ce__steps" aria-label="progress">
        {STEPS.map((s, i) => (
          <li key={s} className={`ce__step ${i === step ? "is-current" : ""} ${i < step ? "is-done" : ""}`}>
            <button type="button" className="ce__step-btn" disabled={i > step && !stepValid.slice(0, i).every(Boolean)} onClick={() => setStep(i)}>
              <span className="ce__step-no">{i < step ? "✓" : String(i + 1)}</span>
              <span className="ce__step-lbl">{s}</span>
            </button>
            {i < STEPS.length - 1 && <span className="ce__step-line" />}
          </li>
        ))}
      </ol>

      <div className="ce__grid">
        {/* Step body — full width of the left column */}
        <section className="ce__panel" key={step}>
          {step === 0 && (
            <div className="ce__body">
              <StepHead n="01" name="Identity" sub="the human label inscribed alongside the on-chain AgentCap" />
              <Field label="Agent name" help="Shows in the fleet and the audit log. Not the on-chain id.">
                <input className="ce__in" value={policy.name} placeholder="deepbook-mm-1" onChange={(e) => update((p) => ({ ...p, name: e.target.value }))} />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="ce__body">
              <StepHead n="02" name="Budget" sub="one row per coin — a daily ceiling, optional per-tx. First coin is wallet-funded; the rest fund as the agent earns them." />
              <div className="ce__bud">
                <div className="ce__bud-head"><span>Coin</span><span className="r">Daily cap</span><span className="r">Per-tx</span><span /></div>
                {policy.caps.map((c, i) => (
                  <div className="ce__bud-row" key={i}>
                    <div className="ce__bud-cell">
                      <span className="ce__coin" title={c.coin}>
                        <span className="ce__coin-mark">{(c.coin[0] ?? "?").toUpperCase()}</span>
                        {c.coin.length > 14 ? `${c.coin.slice(0, 6)}…${c.coin.slice(-4)}` : c.coin}
                      </span>
                      {i === 0 && <span className="ce__bud-tag">funded</span>}
                    </div>
                    <input className="ce__in ce__in--num r" type="number" min={0} value={c.per_day} onChange={(e) => setCap(i, { per_day: Number(e.target.value) || 0 })} />
                    <input className="ce__in ce__in--num r" type="number" min={0} value={c.per_tx ?? ""} placeholder="none" onChange={(e) => setCap(i, { per_tx: e.target.value ? Number(e.target.value) : undefined })} />
                    <button type="button" className="ce__bud-x" disabled={policy.caps.length === 1} onClick={() => removeCap(i)} aria-label="remove">×</button>
                  </div>
                ))}
              </div>
              <div className="ce__add">
                <button type="button" className="ce__bud-add" aria-expanded={pickerOpen} onClick={() => setPickerOpen((o) => !o)}>+ Add coin</button>
                {pickerOpen && (
                    <div className="ce__dd" role="menu">
                      {POPULAR_COINS.filter((s) => !usedCoins.has(s)).map((s) => (
                        <button key={s} type="button" className="ce__dd-item" role="menuitem" onClick={() => addCap(s)}>
                          <span className="ce__dd-mark">{s[0]}</span>
                          <span className="ce__dd-sym">{s}</span>
                          <span className="ce__dd-name">{COIN_NAME[s] ?? "Coin"}</span>
                        </button>
                      ))}
                      {POPULAR_COINS.every((s) => usedCoins.has(s)) && <div className="ce__dd-none">all popular coins added — paste a coin-type below</div>}
                      <div className="ce__dd-paste">
                        <input className="ce__in" spellCheck={false} placeholder="paste coin-type  0x…::mod::TYPE" value={pasteCoin} onChange={(e) => setPasteCoin(e.target.value)} />
                        <button type="button" className="ce__pick-add" disabled={!pasteCoin.includes("::")} onClick={() => addCap(pasteCoin.trim())}>Add</button>
                      </div>
                    </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="ce__body">
              <StepHead n="03" name="Capabilities" sub="default-deny — an action not enabled here aborts on-chain, regardless of budget" />
              {ACTION_GROUPS.map((g) => (
                <div className="ce__cap-group" key={g.group}>
                  <div className="ce__cap-ghead"><span className="ce__cap-gname">{g.group}</span><span className="ce__cap-gverify">{g.verify}</span></div>
                  <div className="ce__checks">
                    {g.ids.map((id) => {
                      const a = ACTIONS.find((x) => x.id === id)!;
                      const on = policy.allowed_actions.includes(a.id);
                      return (
                        <label key={a.id} className={`ce__check ${on ? "is-on" : ""} ${a.id === "transfer" ? "is-danger" : ""}`}>
                          <input type="checkbox" checked={on} onChange={() => toggleAction(a.id)} />
                          <span className="ce__check-box" aria-hidden><svg viewBox="0 0 12 12" width="11" height="11"><path d="M2.5 6.2l2.2 2.3L9.6 3.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                          <span className="ce__check-main"><span className="ce__check-lbl">{a.label}</span><span className="ce__check-hint">{a.hint}</span></span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
              {swapOn && (
                <div className="ce__param">
                  <Field label="Slippage" help="Max a swap may lose to price movement before it aborts on-chain.">
                    <div className="ce__inline"><input className="ce__in ce__in--num" type="number" step="0.1" min={0} max={50} value={slipPct} onChange={(e) => update((p) => ({ ...p, action_params: { ...p.action_params, deepbook_swap_min_rate: 1 - (Number(e.target.value) || 0) / 100 } }))} /><span className="ce__unit">% max slippage</span></div>
                  </Field>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="ce__body">
              <StepHead n="04" name="Scope" sub="allowed_packages — the only protocol the session key may call" />
              <Field label="Protocol" help="Locked to DeepBook for this cycle. The agent cannot touch any other package.">
                <span className="ce__locked">DeepBook<small>package locked</small></span>
              </Field>
              <Field label="Pool" help="Leave blank to allow any DeepBook pool, or pin a single pool id.">
                <input className="ce__in" value={policy.scope.pool ?? ""} placeholder="0x… (optional)" onChange={(e) => update((p) => { const pool = e.target.value; return pool ? { ...p, scope: { ...p.scope, pool } } : { ...p, scope: { pkg: p.scope.pkg } }; })} />
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="ce__body">
              <StepHead n="05" name="Lifetime" sub="expires_at_ms — authority lapses automatically, no action needed" />
              <Field label="Expiry" help={<>Authority lapses at <b className="ce__when">{expiryLabel(policy.expiry_hours)}</b>. After that every action aborts.</>}>
                <div className="ce__inline"><input className="ce__in ce__in--num" type="number" min={0} value={policy.expiry_hours} onChange={(e) => update((p) => ({ ...p, expiry_hours: Number(e.target.value) || 0 }))} /><span className="ce__unit">hours</span></div>
              </Field>
            </div>
          )}

          {step === 5 && (
            <div className="ce__body">
              <StepHead n="06" name="Review" sub="this is the exact policy object that gets minted on Sui" />
              <ul className="ce__review">
                <li><span>Agent</span><b>{policy.name}</b></li>
                {policy.caps.map((c, i) => (
                  <li key={i}><span>{i === 0 ? "Budget" : "Cap"} · {c.coin}</span><b>{c.per_day}/day{c.per_tx != null ? ` · ${c.per_tx}/tx` : ""}</b></li>
                ))}
                <li><span>Actions</span><b>{policy.allowed_actions.map((a) => ACTIONS.find((x) => x.id === a)?.label ?? a).join(", ") || "none"}</b></li>
                {swapOn && <li><span>Slippage</span><b>{slipPct}% max</b></li>}
                <li><span>Scope</span><b>deepbook{policy.scope.pool ? ` · ${policy.scope.pool.slice(0, 8)}…` : " · any pool"}</b></li>
                <li><span>Expires</span><b>{expiryLabel(policy.expiry_hours)}</b></li>
              </ul>
            </div>
          )}
          <nav className="ce__nav">
            <button type="button" className="ce__back" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>← Back</button>
            <span className="ce__progress">{String(step + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}</span>
            {isReview ? (
              <button type="button" className={`ce__mint ${minted ? "is-done" : ""}`} disabled={!stepValid.every(Boolean)} onClick={() => { setMinted(true); onSubmit(policy); }}>{minted ? "✓ Minting…" : "Mint policy →"}</button>
            ) : (
              <button type="button" className="ce__next" disabled={!canNext} onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}>Next →</button>
            )}
          </nav>
        </section>

        {/* Policy object — the artifact, sticky, fills the right column */}
        <aside className="ce__obj">
          <div className="ce__obj-head">
            <span>Policy object</span>
            <button type="button" className="ce__obj-toggle" onClick={() => (jsonEdit ? setJsonEdit(false) : openEditJson())}>{jsonEdit ? "preview" : "edit / paste json"}</button>
          </div>
          {jsonEdit ? (
            <>
              <textarea className="ce__obj-edit" value={jsonText} spellCheck={false} onChange={(e) => setJsonText(e.target.value)} />
              <div className="ce__obj-foot">
                {jsonErr && <span className="ce__obj-err">{jsonErr}</span>}
                <button type="button" className="ce__obj-apply" onClick={applyJson}>Apply →</button>
              </div>
            </>
          ) : (
            <div className="ce__obj-read">
              {policy.caps.map((c, i) => (
                <div key={i}><span>{i === 0 ? "Budget" : "Cap"} · {c.coin}</span><b>{c.per_day}/day{c.per_tx != null ? ` · ${c.per_tx}/tx` : ""}</b></div>
              ))}
              <div><span>Can</span><b>{policy.allowed_actions.map((a) => ACTIONS.find((x) => x.id === a)?.label).join(", ") || "nothing"}</b></div>
              {swapOn && <div><span>Slippage</span><b>{slipPct}% max</b></div>}
              <div><span>Scope</span><b>DeepBook · {policy.scope.pool ? "pinned pool" : "any pool"}</b></div>
              <div><span>Expires</span><b>{expiryLabel(policy.expiry_hours)}</b></div>
            </div>
          )}
          <div className="ce__obj-note">Minting provisions the vault + this policy object + the AgentCap in one transaction.</div>
        </aside>
      </div>


      <style>{`
        .ce { max-width: var(--page-max); margin: 0 auto; font-family: var(--font-body); color: var(--bone); }
        .ce__head { margin-bottom: var(--s-5); }
        .ce__kick { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--accent-deep); }
        .ce__title { font: var(--type-display-page); font-style: italic; margin: var(--s-2) 0 0; }

        /* Step rail */
        .ce__steps { list-style: none; display: flex; align-items: center; margin: 0 0 var(--s-6); padding: 0; }
        .ce__step { display: flex; align-items: center; flex: 1; min-width: 0; }
        .ce__step-btn { display: inline-flex; align-items: center; gap: var(--s-2); background: none; border: 0; color: var(--slate-3); white-space: nowrap; transition: color 150ms ease; }
        .ce__step-btn:disabled { cursor: default; }
        .ce__step-no { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; border: 1px solid var(--line); font: var(--type-mono-address); flex: none; transition: all 180ms cubic-bezier(0.23,1,0.32,1); }
        .ce__step-lbl { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; }
        .ce__step.is-current .ce__step-btn, .ce__step.is-done .ce__step-btn { color: var(--bone); }
        .ce__step.is-current .ce__step-no { border-color: var(--indigo); background: var(--indigo); color: var(--text-on-ink); }
        .ce__step.is-done .ce__step-no { border-color: var(--ok); color: var(--ok); }
        .ce__step-line { flex: 1; height: 1px; background: var(--line); margin: 0 var(--s-3); min-width: 12px; }
        .ce__step.is-done .ce__step-line { background: var(--ok); }

        .ce__grid { display: grid; grid-template-columns: 1fr 380px; gap: var(--s-6); align-items: start; }

        /* fixed-height card + nav pinned: Back/Next land at the exact same Y on
           every step. Body overflow is a safety net; steps are sized to fit. */
        .ce__panel { display: flex; flex-direction: column; height: 624px; background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--r-md); padding: var(--s-6) var(--s-7); animation: ce-in 220ms cubic-bezier(0.23,1,0.32,1); }
        .ce__nav { display: flex; align-items: center; justify-content: space-between; gap: var(--s-5); margin-top: auto; padding-top: var(--s-5); border-top: 1px solid var(--line-soft); }
        @keyframes ce-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .ce__body { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: var(--s-4); }

        .ce__sh { margin-bottom: var(--s-2); }
        .ce__sh-row { display: flex; align-items: baseline; gap: var(--s-3); }
        .ce__sh-no { font: var(--type-mono-address); color: var(--accent-deep); letter-spacing: 0.12em; }
        .ce__sh-name { font: var(--type-display-section); font-style: italic; font-size: 22px; margin: 0; }
        .ce__sh-sub { font: var(--type-body-small); color: var(--slate-2); margin: var(--s-2) 0 0; max-width: 60ch; line-height: 1.5; }

        .ce__field { display: flex; flex-direction: column; gap: var(--s-2); }
        .ce__field-top { display: flex; align-items: center; justify-content: space-between; }
        .ce__field-lbl { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); }
        .ce__field-help { font: var(--type-body-small); color: var(--slate-2); line-height: 1.5; max-width: 62ch; }
        .ce__when { font-family: var(--font-mono); font-style: normal; color: var(--bone-2); }

        .ce__in { width: 100%; max-width: 360px; font: var(--type-body); color: var(--bone); background: var(--bg); border: 1px solid var(--line); border-radius: var(--r-sm); padding: 10px 12px; transition: border-color 150ms ease; }
        .ce__in:focus { outline: none; border-color: var(--indigo); }
        .ce__in--num { max-width: 150px; font-family: var(--font-mono); font-variant-numeric: tabular-nums; }
        .ce__in:disabled { background: var(--bg-elev); color: var(--slate-3); }
        .ce__inline { display: flex; align-items: center; gap: var(--s-3); }
        .ce__unit { font: var(--type-mono-data); color: var(--slate-2); }
        .ce__in--num.r { text-align: right; }
        .ce__bud { display: flex; flex-direction: column; }
        .ce__bud-head { display: grid; grid-template-columns: 1.4fr 1fr 1fr 32px; gap: var(--s-3); align-items: center; padding-bottom: 8px; }
        .ce__bud-head span { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); }
        .ce__bud-head .r { text-align: right; }
        .ce__bud-row { display: grid; grid-template-columns: 1.4fr 1fr 1fr 32px; gap: var(--s-3); align-items: center; padding: 8px 0; border-top: 1px solid var(--line-soft); }
        .ce__bud-cell { display: flex; align-items: center; gap: var(--s-2); }
        .ce__bud-tag { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.06em; color: var(--accent-deep); }
        .ce__bud-x { width: 28px; height: 28px; border: 1px solid var(--line); border-radius: var(--r-sm); color: var(--slate-2); font-size: 16px; line-height: 1; transition: border-color 150ms ease, color 150ms ease, transform 120ms cubic-bezier(0.23,1,0.32,1); }
        .ce__bud-x:hover:not(:disabled) { border-color: var(--oxblood); color: var(--oxblood); }
        .ce__bud-x:active:not(:disabled) { transform: scale(0.94); }
        .ce__bud-x:disabled { opacity: 0.3; cursor: default; }
        .ce__bud-add { align-self: flex-start; margin-top: var(--s-2); font: var(--type-label); letter-spacing: 0.04em; text-transform: uppercase; color: var(--indigo); border: 1px solid var(--line); border-radius: var(--r-sm); padding: 8px 14px; transition: border-color 150ms ease, background 150ms ease; }
        .ce__bud-add:hover { border-color: var(--indigo); background: var(--indigo-tint); }
        .ce__coin { display: inline-flex; align-items: center; gap: 8px; font: var(--type-mono-data); font-weight: 600; color: var(--bone); padding: 6px 14px 6px 7px; border: 1px solid var(--line); border-radius: var(--r-pill); background: var(--bg); }
        .ce__coin-mark { width: 20px; height: 20px; border-radius: 50%; display: grid; place-items: center; background: var(--indigo-tint); color: var(--indigo); font-size: 10px; font-weight: 700; }
        .ce__add { align-self: flex-start; margin-top: var(--s-2); }
        .ce__dd { margin-top: 8px; width: 320px; max-width: 100%; background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--r-md); box-shadow: 0 10px 28px -10px color-mix(in srgb, var(--indigo) 20%, transparent); padding: 6px; transform-origin: top left; animation: ce-dd 160ms cubic-bezier(0.23,1,0.32,1); }
        @keyframes ce-dd { from { opacity: 0; transform: scale(0.98) translateY(-4px); } to { opacity: 1; transform: none; } }
        .ce__dd-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 7px 10px; border: 0; background: transparent; border-radius: var(--r-sm); text-align: left; transition: background 120ms ease, transform 110ms ease; }
        .ce__dd-item:hover { background: var(--indigo-tint); } .ce__dd-item:active { transform: scale(0.99); }
        .ce__dd-mark { width: 24px; height: 24px; border-radius: 50%; display: grid; place-items: center; background: color-mix(in srgb, var(--accent) 16%, transparent); color: var(--accent-deep); font: var(--type-mono-data); font-weight: 700; font-size: 11px; }
        .ce__dd-sym { font: var(--type-mono-data); font-weight: 600; color: var(--bone); }
        .ce__dd-name { margin-left: auto; font: var(--type-label); color: var(--slate-2); }
        .ce__dd-none { padding: 8px 10px 10px; font: var(--type-label); color: var(--slate-2); }
        .ce__dd-paste { display: flex; gap: 6px; padding: 8px 4px 4px; border-top: 1px solid var(--line-soft); margin-top: 4px; }
        .ce__dd-paste .ce__in { flex: 1; font: var(--type-mono-data); font-size: 12px; }
        .ce__pick-add { font: var(--type-label); text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-on-ink); background: var(--indigo); border: 0; border-radius: var(--r-sm); padding: 0 16px; transition: opacity 150ms ease, transform 120ms ease; }
        .ce__pick-add:disabled { opacity: 0.4; } .ce__pick-add:active:not(:disabled) { transform: scale(0.97); }

        .ce__seg { display: inline-flex; border: 1px solid var(--line); border-radius: var(--r-sm); overflow: hidden; }
        .ce__seg button { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.06em; padding: 9px 16px; border-right: 1px solid var(--line); background: var(--bg); color: var(--slate); transition: background 150ms ease, color 150ms ease; }
        .ce__seg button:last-child { border-right: 0; }
        .ce__seg button.is-on { background: var(--bone); color: var(--text-on-ink); }

        .ce__toggle { position: relative; width: 38px; height: 22px; border: 1px solid var(--line); border-radius: 999px; background: var(--bg); transition: background 150ms ease, border-color 150ms ease; flex: none; }
        .ce__toggle.is-on { background: var(--indigo); border-color: var(--indigo); }
        .ce__toggle-k { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: var(--slate-3); transition: transform 150ms cubic-bezier(0.23,1,0.32,1); }
        .ce__toggle.is-on .ce__toggle-k { transform: translateX(16px); background: var(--text-on-ink); }

        .ce__checks { display: grid; grid-template-columns: 1fr 1fr; gap: var(--s-2); }
        .ce__check { display: flex; align-items: center; gap: var(--s-3); padding: 10px 13px; border: 1px solid var(--line); border-radius: var(--r-sm); background: var(--bg); cursor: pointer; transition: border-color 150ms ease, background 150ms ease; }
        .ce__check:hover { border-color: var(--line-stamp); }
        .ce__check.is-on { border-color: var(--indigo); background: var(--indigo-tint); }
        .ce__check.is-danger.is-on { border-color: var(--oxblood); background: var(--oxblood-tint); }
        .ce__check input { position: absolute; opacity: 0; width: 0; height: 0; }
        .ce__check-box { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border: 1px solid var(--line-stamp); border-radius: 4px; background: var(--bg-elev); color: transparent; flex: none; transition: background 150ms ease, border-color 150ms ease; }
        .ce__check.is-on .ce__check-box { background: var(--indigo); border-color: var(--indigo); color: var(--text-on-ink); }
        .ce__check.is-danger.is-on .ce__check-box { background: var(--oxblood); border-color: var(--oxblood); }
        .ce__check-main { display: flex; flex-direction: column; gap: 1px; }
        .ce__check-lbl { font: var(--type-body); font-weight: 500; }
        .ce__check-hint { font: var(--type-body-small); color: var(--slate-2); }
        .ce__check-id { margin-left: auto; font: var(--type-mono-address); color: var(--slate-3); }
        .ce__param { padding-top: var(--s-4); border-top: 1px solid var(--line-soft); }

        .ce__locked { display: inline-flex; flex-direction: column; gap: 3px; font: var(--type-body); padding: 9px 16px; border: 1px solid var(--line); border-radius: var(--r-sm); background: var(--indigo-tint); }
        .ce__locked small { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.07em; color: var(--slate-2); }

        .ce__review { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
        .ce__review li { display: flex; justify-content: space-between; align-items: baseline; padding: 11px 0; border-bottom: 1px solid var(--line-soft); }
        .ce__review li span { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); }
        .ce__review li b { font: var(--type-mono-data); color: var(--bone); font-weight: 400; }

        /* Policy object artifact */
        .ce__obj { position: sticky; top: var(--s-5); background: var(--bg-elev2); border: 1px solid var(--line); border-radius: var(--r-md); overflow: hidden; }
        .ce__obj-head { display: flex; justify-content: space-between; align-items: baseline; padding: var(--s-4) var(--s-5); border-bottom: 1px solid var(--line-soft); font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); }
        .ce__obj-tag { color: var(--accent-deep); }
        .ce__obj-code { margin: 0; padding: var(--s-5); font: var(--type-mono-data); line-height: 1.65; color: var(--bone-2); white-space: pre; overflow-x: auto; }
        .ce__obj-note { padding: var(--s-4) var(--s-5); border-top: 1px solid var(--line-soft); font: var(--type-body-small); color: var(--slate-2); line-height: 1.5; }
        .ce__obj-toggle { font: var(--type-mono-address); text-transform: lowercase; letter-spacing: 0.04em; color: var(--accent-deep); border-bottom: 1px solid transparent; }
        .ce__obj-toggle:hover { border-bottom-color: var(--accent); }
        .ce__obj-read { padding: var(--s-4) var(--s-5); display: flex; flex-direction: column; gap: 8px; border-bottom: 1px solid var(--line-soft); }
        .ce__obj-read > div { display: flex; align-items: baseline; justify-content: space-between; gap: var(--s-3); }
        .ce__obj-read span { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); }
        .ce__obj-read b { font: var(--type-mono-data); color: var(--bone); font-weight: 400; text-align: right; }
        .ce__obj-edit { width: 100%; min-height: 320px; max-height: 56vh; resize: vertical; padding: var(--s-5); border: 0; border-bottom: 1px solid var(--line-soft); background: var(--bg-elev2); color: var(--bone-2); font: var(--type-mono-data); line-height: 1.6; }
        .ce__obj-edit:focus { outline: none; background: var(--bg); }
        .ce__obj-foot { display: flex; align-items: center; justify-content: space-between; gap: var(--s-3); padding: var(--s-3) var(--s-5); }
        .ce__obj-err { font: var(--type-body-small); color: var(--oxblood); }
        .ce__obj-apply { margin-left: auto; font: var(--type-label); letter-spacing: 0.04em; text-transform: uppercase; padding: 8px 16px; border-radius: var(--r-sm); background: var(--indigo); color: var(--text-on-ink); transition: background 150ms ease; }
        .ce__obj-apply:hover { background: var(--indigo-deep); }
        .ce__cap-group { display: flex; flex-direction: column; gap: var(--s-2); }
        .ce__cap-ghead { display: flex; align-items: baseline; gap: var(--s-3); padding-bottom: 2px; }
        .ce__cap-gname { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--bone-2); }
        .ce__cap-gverify { font: var(--type-body-small); color: var(--slate-2); }

        /* Footer nav */
        .ce__foot { display: flex; align-items: center; justify-content: space-between; gap: var(--s-5); margin-top: var(--s-5); padding: var(--s-4) var(--s-6); background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--r-md); }
        .ce__back { font: var(--type-label); letter-spacing: 0.04em; text-transform: uppercase; color: var(--slate); padding: 10px 16px; border: 1px solid var(--line); border-radius: var(--r-sm); transition: border-color 150ms ease, color 150ms ease, transform 120ms cubic-bezier(0.23,1,0.32,1); }
        .ce__back:hover:not(:disabled) { border-color: var(--line-stamp); color: var(--bone); }
        .ce__back:active:not(:disabled) { transform: scale(0.98); }
        .ce__back:disabled { opacity: 0.4; cursor: default; }
        .ce__progress { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--slate-2); }
        .ce__next, .ce__mint { font: var(--type-label); letter-spacing: 0.04em; text-transform: uppercase; padding: 11px 22px; border-radius: var(--r-sm); background: var(--indigo); color: var(--text-on-ink); transition: background 150ms ease, transform 120ms cubic-bezier(0.23,1,0.32,1); }
        .ce__next:hover:not(:disabled), .ce__mint:hover:not(:disabled) { background: var(--indigo-deep); }
        .ce__next:active:not(:disabled), .ce__mint:active:not(:disabled) { transform: scale(0.98); }
        .ce__next:disabled, .ce__mint:disabled { background: var(--line); color: var(--slate-3); cursor: not-allowed; }
        .ce__mint.is-done { background: var(--ok); }

        @media (max-width: 920px) { .ce__grid { grid-template-columns: 1fr; } .ce__obj { position: static; } .ce__checks { grid-template-columns: 1fr; } }
        @media (prefers-reduced-motion: reduce) { .ce__panel, .ce__step-no, .ce__toggle-k { animation: none !important; transition: none !important; } }
      `}</style>
    </div>
  );
}

function StepHead({ n, name, sub }: { n: string; name: string; sub: string }) {
  return (
    <div className="ce__sh">
      <div className="ce__sh-row"><span className="ce__sh-no">{n}</span><h2 className="ce__sh-name">{name}</h2></div>
      <p className="ce__sh-sub">{sub}</p>
    </div>
  );
}

function Field({ label, help, children, toggle }: { label: string; help?: ReactNode; children: ReactNode; toggle?: { on: boolean; onToggle: () => void } }) {
  return (
    <div className="ce__field">
      <div className="ce__field-top">
        <span className="ce__field-lbl">{label}</span>
        {toggle && <button type="button" className={`ce__toggle ${toggle.on ? "is-on" : ""}`} role="switch" aria-checked={toggle.on} aria-label={`toggle ${label}`} onClick={toggle.onToggle}><span className="ce__toggle-k" /></button>}
      </div>
      {children}
      {help && <p className="ce__field-help">{help}</p>}
    </div>
  );
}
